/**
 * Phase 3 — Local IndexedDB store for offloaded message blobs.
 *
 * Schema: DB "summa-messages", store "offloaded", keyPath "mailId"
 * Each record: { mailId, subject, fromName, sentDate, sizeBytes, blobContent, storedAt }
 *
 * blobContent is the raw string retrieved from Azure — may start with "V2:" (encrypted)
 * or be plaintext.  Reading requires sending it to POST /api/offload/decrypt-local.
 */

const DB_NAME    = 'summa-messages';
const DB_VERSION = 1;
const STORE_NAME = 'offloaded';

export interface OffloadedRecord {
  mailId:      string;
  subject:     string;
  fromName:    string;
  sentDate:    string;
  sizeBytes:   number;
  blobContent: string;   // raw encrypted or plaintext
  storedAt:    string;   // ISO
}

export type OffloadedMeta = Omit<OffloadedRecord, 'blobContent'>;

// ── Open / init ────────────────────────────────────────────────────────────────
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'mailId' });
      }
    };
    req.onsuccess  = (e) => resolve((e.target as IDBOpenDBRequest).result);
    req.onerror    = (e) => reject((e.target as IDBOpenDBRequest).error);
  });
}

function tx(
  db: IDBDatabase,
  mode: IDBTransactionMode
): IDBObjectStore {
  return db.transaction(STORE_NAME, mode).objectStore(STORE_NAME);
}

// ── Public API ────────────────────────────────────────────────────────────────
export async function saveMessage(record: OffloadedRecord): Promise<void> {
  const db    = await openDb();
  const store = tx(db, 'readwrite');
  await new Promise<void>((resolve, reject) => {
    const req = store.put(record);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
  db.close();
}

export async function getMessage(mailId: string): Promise<OffloadedRecord | null> {
  const db    = await openDb();
  const store = tx(db, 'readonly');
  const record = await new Promise<OffloadedRecord | null>((resolve, reject) => {
    const req = store.get(mailId);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror   = () => reject(req.error);
  });
  db.close();
  return record;
}

export async function hasMessage(mailId: string): Promise<boolean> {
  const db    = await openDb();
  const store = tx(db, 'readonly');
  const exists = await new Promise<boolean>((resolve, reject) => {
    const req = store.getKey(mailId);
    req.onsuccess = () => resolve(req.result != null);
    req.onerror   = () => reject(req.error);
  });
  db.close();
  return exists;
}

export async function deleteMessage(mailId: string): Promise<void> {
  const db    = await openDb();
  const store = tx(db, 'readwrite');
  await new Promise<void>((resolve, reject) => {
    const req = store.delete(mailId);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
  db.close();
}

export async function getAllMetadata(): Promise<OffloadedMeta[]> {
  const db    = await openDb();
  const store = tx(db, 'readonly');
  const all   = await new Promise<OffloadedRecord[]>((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result ?? []);
    req.onerror   = () => reject(req.error);
  });
  db.close();
  return all.map(({ blobContent: _blob, ...meta }) => meta);
}

export async function getTotalUsageBytes(): Promise<number> {
  const meta = await getAllMetadata();
  return meta.reduce((sum, r) => sum + r.sizeBytes, 0);
}

/**
 * Download a blob from a SAS URL and store it in IndexedDB.
 * Returns true on success.
 * NOTE: Direct SAS URL fetches may fail due to Azure Blob CORS restrictions.
 * Prefer downloadAndStoreViaApi() which proxies through the API.
 */
export async function downloadAndStore(
  sasUrl: string,
  meta: Omit<OffloadedRecord, 'blobContent' | 'storedAt'>
): Promise<boolean> {
  try {
    const resp = await fetch(sasUrl);
    if (!resp.ok) return false;
    const blobContent = await resp.text();
    await saveMessage({
      ...meta,
      blobContent,
      storedAt: new Date().toISOString(),
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Download a message body through the API proxy (avoids Azure Blob CORS issues)
 * and store it in IndexedDB. Returns true on success.
 */
export async function downloadAndStoreViaApi(
  mailId: string,
  meta: Omit<OffloadedRecord, 'blobContent' | 'storedAt'>,
  apiFetch: (path: string) => Promise<string>
): Promise<boolean> {
  try {
    const blobContent = await apiFetch(`/api/offload/${mailId}/body`);
    if (!blobContent) return false;
    await saveMessage({
      ...meta,
      blobContent,
      storedAt: new Date().toISOString(),
    });
    return true;
  } catch {
    return false;
  }
}
